import { DestroyRef, Directive, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AppState } from '@app/reducers/interfaces';
import { Store } from '@ngrx/store';
import { EnhancedAbsenceModel } from '@reducers/orm/absence/absence.model';
import { getEnhancedAbsenceById } from '@reducers/orm/absence/absence.service';
import { FeatureService } from '@sb/shared/feature';

import { AbsenceRequestAction, AbsenceRequestOriginator } from './absence-request.models';

@Directive({
  selector: '[appAbsenceRequest]',
  host: {
    class: 'cursor-pointer',
    '(click)': 'onClick($event)',
    '(keydown.enter)': 'onKeydown($event)',
    '(keydown.space)': 'onKeydown($event)',
  },
})
export class AbsenceRequestDirective implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private feature = inject(FeatureService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  @Input()
  set appAbsenceRequest(context: {
    absenceId: string;
    isOwnAbsence?: boolean;
    action?: AbsenceRequestAction;
    originator: AbsenceRequestOriginator;
  }) {
    this.absenceId = context.absenceId;
    this.originator = context.originator;
    if (context.action) {
      this.action = context.action;
    }
  }

  onClick(event: Event) {
    event.stopPropagation();
    switch (this.action) {
      case 'edit':
        this.openEditModal();
        break;
      default:
        this.openRequestModal();
    }
  }

  /**
   * Enter/Space open the request too, for a host made focusable (`tabindex="0"`). Guarded to the host
   * itself so a focused control inside it keeps its own activation.
   */
  onKeydown(event: Event) {
    if (event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();
    this.onClick(event);
  }

  private absenceId: string;
  private absence: EnhancedAbsenceModel;
  private action: AbsenceRequestAction = 'view';
  private originator: AbsenceRequestOriginator;

  ngOnInit(): void {
    this.setAbsence();
  }

  private openRequestModal() {
    this.router.navigate([
      '',
      { outlets: { modal: ['absence-request', this.absenceId, { originator: this.originator }] } },
    ]);
  }

  private openEditModal() {
    this.router.navigate([
      '',
      { outlets: { modal: ['absence-edit', this.absenceId, { originator: this.originator }] } },
    ]);
  }

  private setAbsence() {
    void this.store
      .select(getEnhancedAbsenceById(this.absenceId))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((absence: EnhancedAbsenceModel) => (this.absence = absence));
  }
}
